import { STORE_TIMEZONE, isDayOnly } from "@estelle/domain";

/**
 * A filter's date-and-time value, taken apart and put together (#555).
 *
 * Two shapes, both of which the filter engines already read. A day with no
 * time, `2026-09-30`, is the whole of that day in the store's timezone. A day
 * with a time is written with no zone, `2026-09-30T10:30`, which both engines
 * read off the store's clock (#519) — so the filter holds the time as it was
 * picked, and "after 10:30" is the same half past ten for someone reading
 * from another country as for the warehouse (#406).
 *
 * A value that does carry a zone, from a link built by hand, is an instant;
 * it is shown as the store's clock had it.
 */
export interface DayAndTime {
  /** `YYYY-MM-DD`, or `""`. */
  day: string;
  /** `HH:mm` on the store's clock, or `""` for the whole day. */
  time: string;
}

const STORE_CLOCK = new Intl.DateTimeFormat("en-CA", {
  timeZone: STORE_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function storeParts(ms: number): DayAndTime {
  const parts: Record<string, string> = {};
  for (const part of STORE_CLOCK.formatToParts(ms)) {
    parts[part.type] = part.value;
  }
  return {
    day: `${parts.year ?? ""}-${parts.month ?? ""}-${parts.day ?? ""}`,
    time: `${parts.hour ?? "00"}:${parts.minute ?? "00"}`,
  };
}

const ZONELESS = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?$/;

export function splitDayAndTime(value: string): DayAndTime {
  if (value === "") return { day: "", time: "" };
  if (isDayOnly(value)) return { day: value, time: "" };
  const zoneless = ZONELESS.exec(value);
  if (zoneless) return { day: zoneless[1] ?? "", time: zoneless[2] ?? "" };
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? { day: "", time: "" } : storeParts(ms);
}

export function joinDayAndTime({ day, time }: DayAndTime): string {
  return day === "" || time === "" ? day : `${day}T${time}`;
}

/**
 * A time as someone types it — `9`, `9:30`, `0930`, `9.30 pm`, `21:30` — as
 * `HH:mm`. Undefined for anything that is not a time of day.
 */
export function parseTypedTime(text: string): string | undefined {
  const match = /^(\d{1,2})(?:[:.]?(\d{2}))?\s*(am|pm)?$/i.exec(text.trim());
  if (!match) return undefined;
  let hour = Number(match[1]);
  const minute = Number(match[2] ?? "0");
  const half = match[3]?.toLowerCase();
  if (half) {
    if (hour < 1 || hour > 12) return undefined;
    hour = (hour % 12) + (half === "pm" ? 12 : 0);
  }
  if (hour > 23 || minute > 59) return undefined;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** `10:30` as `10:30 am`, the way the tables word a time. */
export function showTime(time: string): string {
  const [hour, minute] = time.split(":").map(Number);
  if (hour === undefined || minute === undefined) return time;
  const half = hour < 12 ? "am" : "pm";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(twelve)}:${String(minute).padStart(2, "0")} ${half}`;
}

/** Every quarter hour of a day, `00:00` to `23:45`. */
export const QUARTER_HOURS: readonly string[] = Array.from(
  { length: 96 },
  (_, index) =>
    `${String(Math.floor(index / 4)).padStart(2, "0")}:${String((index % 4) * 15).padStart(2, "0")}`,
);
